import React from 'react';
import { useTranslation } from 'react-i18next';
import { Organization } from '../../models';
import { requestCount } from '../../utils/utils';
import { useAuth } from 'react-oidc-context';
import { FilterSetController, Card, ContentCard, ToastContext, Paginator } from '@weezevent/nacre';

import { SauronEmptyCard, LoadingCard } from '../../components';
import OrganizationCard from './OrganizationCard';

import css from './list.module.css';

export default function OrganizationList() {
    const { t } = useTranslation();
    const {
        user: { profile }
    } = useAuth();
    const toast = React.useContext(ToastContext);
    const [currentPage, setCurrentPage] = React.useState(0);
    const [defaultPagination] = React.useState(() => ({ limit: 10, offset: 0 }));
    const [queryParams, setQueryParams] = React.useState({ search: '', ...defaultPagination });

    const query = React.useMemo(
        () => ({
            ...queryParams,
            // instead of searching "O555" (no result), search "555"
            search: queryParams.search.match(/^O[0-9]+$/i) ? queryParams.search.slice(1) : queryParams.search
        }),
        [queryParams]
    );

    const [organizations, loadingOrganizations] = Organization.useApiModel(
        {
            cache: false,
            allow_cache: false,
            query,
            strict: true,
            onError: () => {
                toast.error(t('sauron.toasts.error'));
            }
        },
        [queryParams]
    );

    let handleSearch = React.useCallback(
        (_, value) => {
            setQueryParams({ search: value, ...defaultPagination });
        },
        [defaultPagination]
    );

    const handlePage = React.useCallback(
        value => {
            setCurrentPage(value);
            setQueryParams({ ...queryParams, offset: queryParams.limit * value });
        },
        [queryParams]
    );

    const organizationsCount = React.useMemo(() => {
        return requestCount(loadingOrganizations, organizations);
    }, [organizations, loadingOrganizations, requestCount]);

    const name = profile.given_name || profile.name || profile.data?.firstname || '';
    return (
        <>
            <div>
                <div className={css['header-title']}>
                    {t('sauron.organizations.header-title')}
                    <span className={css['header-user-name']}>{name}</span>
                </div>
                <div className={css['header-subtitle']}>{t('sauron.organizations.header-subtitle')}</div>
            </div>
            <Card>
                <ContentCard>
                    <FilterSetController
                        total={organizationsCount}
                        isLoadingTotal={loadingOrganizations}
                        labelTotal={t('sauron.organizations.organization', { count: organizationsCount })}
                        onChange={() => {}}
                        search={{
                            onChange: (_, value) => {
                                handleSearch(_, value);
                            },
                            handleReset: () => {
                                handleSearch('');
                            },
                            placeholder: t('sauron.organizations.search'),
                            value: queryParams.search
                        }}
                    />
                </ContentCard>
            </Card>
            <LoadingCard loading={loadingOrganizations}>
                {organizations?.length > 0 ? (
                    <>
                        {organizations.map(organization => (
                            <OrganizationCard key={organization.id} organization={organization} />
                        ))}
                        <Paginator style={{ padding: 15 }} currentPage={currentPage} onPageChange={handlePage} pageSize={queryParams.limit} totalCount={organizationsCount} />
                    </>
                ) : (
                    <SauronEmptyCard title={t('sauron.organizations.empty-card-title')} subTitle={t('sauron.organizations.empty-card-subtitle')} />
                )}
            </LoadingCard>
        </>
    );
}
